/**
 * lazyWithRetry — drop-in replacement for React.lazy() that auto-recovers
 * from stale dynamic-import chunks after a deploy.
 *
 * Why this exists: each ``lazy(() => import('./pages/Foo'))`` call in
 * App.tsx compiles to its own content-hashed chunk (e.g.
 * ``Foo-abc123.js``). When the server deploys a new build, that hash
 * changes (``Foo-def456.js``) and the old file is gone from the CDN.
 * Browser tabs that loaded ``index.html`` before the deploy still hold
 * references to the *old* chunk URL — so the first navigation into a
 * route they haven't visited yet throws ``Failed to fetch dynamically
 * imported module``, Suspense bubbles it to RouteErrorBoundary, and the
 * user sees "Something went wrong" with the (misleading) "session may
 * have expired" copy.
 *
 * The cure is just a hard reload — that fetches a fresh ``index.html``
 * with the new chunk URLs. This wrapper detects the stale-chunk error
 * pattern and does the reload automatically instead of surfacing the
 * generic error screen. A sessionStorage flag prevents an infinite
 * reload loop in the rare case the chunk genuinely can't be fetched
 * (e.g. CDN-wide outage) — after a successful import we clear the flag
 * so future deploys can trigger another auto-reload.
 *
 * Non-chunk errors (real render bugs, type errors inside the loaded
 * module's top-level evaluation) re-throw normally and land at the
 * boundary as before.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Browser-specific phrasings of the same failure mode. Match
// case-insensitively; some browsers wrap the underlying network error,
// some surface it directly.
const CHUNK_ERROR_PATTERN =
  /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i;

const RELOAD_FLAG = 'xyden:chunk-reload';

function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && CHUNK_ERROR_PATTERN.test(err.message);
}

// Use ComponentType<any> here (mirroring React.lazy's own signature) so
// callers can pass components with non-trivial prop shapes — narrowing
// to ComponentType<unknown> rejects any component whose props aren't
// literally `unknown`, which is every real component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Reaching the success path proves the latest deploy's chunks are
      // reachable from this tab. Drop the flag so the *next* deploy that
      // breaks a chunk can trigger another auto-reload.
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        // sessionStorage can throw in private/incognito modes — fall
        // through; the wrapper still works, we just can't gate reloads.
      }
      return mod;
    } catch (err) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
      } catch {
        // Same fallback as above. Treat unreadable storage as "haven't
        // reloaded yet" so the user still gets the recovery on first
        // failure; the only downside is we can't suppress a second
        // attempt — acceptable.
      }

      if (isChunkLoadError(err) && !alreadyReloaded) {
        try {
          sessionStorage.setItem(RELOAD_FLAG, '1');
        } catch {
          // Best-effort — proceed with the reload regardless.
        }
        window.location.reload();
        // Return a promise that never settles so React doesn't try to
        // render any fallback content before the reload tears the page
        // down. The Suspense boundary keeps showing its loading state
        // for the brief moment until reload() actually triggers.
        return new Promise<{ default: T }>(() => { /* never resolves */ });
      }

      // Real bug, or we've already attempted a reload in this session
      // and the import is still failing — surface to the boundary.
      throw err;
    }
  });
}
